// Port of the TrailDataset helpers and TrailRoute.init(stages:name:reversed:) of
// CoreModels/Dataset/TrailDataset.swift.

import { cumulativeDistances, distance, joinPolylines, polylineReversed } from '../core/geo';
import type { DataProvenance, GeoCoordinate, TrailDataset, TrailRoute, TrailStage } from '../core/models';

/**
 * Builds a route from ordered stages (joins their polylines). `stageStartDistances` are measured along the joined
 * polyline, so a gap between non-contiguous stages (bridged by a straight connector) is accounted for.
 */
export function trailRouteFromStages(
  stages: readonly TrailStage[],
  options: { name?: string; reversed?: boolean } = {},
): TrailRoute {
  const reversed = options.reversed ?? false;
  const ordered = reversed ? stages.slice().reverse() : stages.slice();
  const lines = ordered.map((s) => (reversed ? polylineReversed(s.polyline) : s.polyline));
  const joined = joinPolylines(lines);
  const cumulative = cumulativeDistances(joined);
  // Mirror `joinPolylines` to find the vertex of `joined` at which each stage starts.
  const starts: number[] = [];
  let emitted = 0;
  let previousEnd: GeoCoordinate | undefined;
  for (const line of lines) {
    const first = line.points[0];
    const last = line.points[line.points.length - 1];
    if (!first || !last) {
      starts.push(emitted > 0 ? cumulative[emitted - 1] : 0);
      continue;
    }
    if (previousEnd && distance(previousEnd, first.coordinate) < 1) {
      // Shared joint vertex: the stage starts where the previous one ended.
      starts.push(cumulative[emitted - 1]);
      emitted += line.points.length - 1;
    } else {
      starts.push(cumulative[emitted]);
      emitted += line.points.length;
    }
    previousEnd = last.coordinate;
  }
  const f = ordered[0];
  const l = ordered[ordered.length - 1];
  const defaultName = f && l ? `${reversed ? f.finish.name : f.start.name} – ${reversed ? l.start.name : l.finish.name}` : '';
  const route: TrailRoute = {
    id: (reversed ? 'rev:' : '') + ordered.map((s) => s.id).join('+'),
    name: options.name ?? defaultName,
    stageIDs: ordered.map((s) => s.id),
    isReversed: reversed,
    polyline: joined,
    stageStartDistances: starts,
  };
  const trailID = stages[0]?.trailID;
  if (trailID !== undefined) route.trailID = trailID;
  return route;
}

/**
 * Stages of a trail in canonical order (Swift `TrailDataset.stages(forTrail:)`): the trail's `stageIDs` order when
 * the trail lists its stages, otherwise the stages referencing `trailID`, sorted by number (then id).
 */
export function stagesForTrail(dataset: TrailDataset, trailID: string): TrailStage[] {
  const trail = dataset.trails.find((tr) => tr.id === trailID);
  if (!trail || trail.stageIDs.length === 0) {
    return dataset.stages
      .filter((s) => s.trailID === trailID)
      .sort((a, b) => (a.number !== b.number ? a.number - b.number : a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  }
  const byID = new Map<string, TrailStage>();
  for (const s of dataset.stages) if (!byID.has(s.id)) byID.set(s.id, s);
  const out: TrailStage[] = [];
  for (const id of trail.stageIDs) {
    const s = byID.get(id);
    if (s) out.push(s);
  }
  return out;
}

/** Value identity of a provenance record (Swift `Hashable` over every field). */
export function provenanceKey(p: DataProvenance): string {
  return JSON.stringify([
    p.provider,
    p.dataset,
    p.sourceURL ?? null,
    p.license ?? null,
    p.attribution ?? null,
    p.fetchedAt,
    p.sourceUpdatedAt ?? null,
    p.isSynthetic,
    p.licenseReviewStatus,
  ]);
}

/** Value equality of two provenance records. */
export function sameProvenance(a: DataProvenance, b: DataProvenance): boolean {
  return a === b || provenanceKey(a) === provenanceKey(b);
}

/** All distinct provenance records of the dataset, in first-appearance order (Sources & Licences screen). */
export function allProvenance(dataset: TrailDataset): DataProvenance[] {
  const seen = new Set<string>();
  const seenObjects = new Set<DataProvenance>();
  const out: DataProvenance[] = [];
  const add = (p: DataProvenance) => {
    if (seenObjects.has(p)) return; // shared records (pack POIs) skip the key computation
    seenObjects.add(p);
    const key = provenanceKey(p);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(p);
  };
  dataset.metadata.provenance.forEach(add);
  for (const r of dataset.trails) add(r.provenance);
  for (const r of dataset.stages) add(r.provenance);
  for (const r of dataset.pointsOfInterest) add(r.provenance);
  for (const r of dataset.protectedAreas) add(r.provenance);
  for (const r of dataset.regulations) add(r.provenance);
  for (const r of dataset.regulatoryCoverage) add(r.provenance);
  if (dataset.terrain) add(dataset.terrain.provenance);
  return out;
}
